"use client"
import { LocationContext } from "@/components/framework/locationswitcher/locationReducer";
import { getLocationSearchReset, type LocationSearchKeys } from "@/components/framework/locationswitcher/locationSearchReset";
import LocationSearchResult from "@/components/framework/locationswitcher/LocationSearchResult";
import RegionOverview from "@/components/framework/locationswitcher/RegionOverview";
import SelectedLocations from "@/components/framework/locationswitcher/SelectedLocations";
import LocalImage from "@/components/image/LocalImage";
import Close from "@/components/images/Close";
import getRegionTheaters from "@/lib/client/getRegionTheaters";
import useClickOutside from "@/lib/useClickOutside";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { ChangeEventHandler, KeyboardEventHandler, use, useEffect, useState } from "react";
import styles from "./LocationSearch.module.css";

type LocationSearchProps = {
    className?: string;
}

//#region [Component]
export default function LocationSearch({ className }: LocationSearchProps) {

    const [inputValue, setInputValue] = useState("");
    const [debouncedInputValue, setDebouncedInputValue] = useState("");

    const locationState = use(LocationContext);

    useEffect(() => {
        const handle = setTimeout(() => {
            setDebouncedInputValue(inputValue);
        }, 250);
        return () => { clearTimeout(handle); }
    }, [inputValue]);

    // Clear the search when the tools close or the selection changes. Done during render
    // (not in an effect) so it doesn't cost an extra render: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    const [prevLocationState, setPrevLocationState] = useState<LocationSearchKeys>({ selected: locationState.selected, isShowingLocationTools: locationState.isShowingLocationTools });
    if (prevLocationState.selected !== locationState.selected || prevLocationState.isShowingLocationTools !== locationState.isShowingLocationTools) {
        const reset = getLocationSearchReset(prevLocationState, locationState);
        setPrevLocationState({ selected: locationState.selected, isShowingLocationTools: locationState.isShowingLocationTools });
        if (reset.input) {
            setInputValue("");
        }
        if (reset.debounced) {
            setDebouncedInputValue("");
        }
    }

    const ref = useClickOutside<HTMLDivElement>(() => locationState.dispatcher({ type: "toggleToolsDisplay", value: false }));

    const { data } = useQuery({
        queryFn: getRegionTheaters,
        queryKey: ["theaters"],
        enabled: locationState.isShowingLocationTools
    });

    const changeEventHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
        setInputValue(event.target.value);
    };

    const onKeyHandler: KeyboardEventHandler<HTMLInputElement> = (event) => {
        switch (event.key) {
            case "Enter":
                event.preventDefault();
                break;
            case "Escape":
                event.preventDefault();
                locationState.dispatcher({ type: "toggleToolsDisplay", value: false });

                break;
            default:
                locationState.dispatcher({ type: "toggleToolsDisplay", value: true });

        }

    };

    return <div className={classNames(styles.SearchContainer, locationState.isShowingLocationTools ? styles.open : undefined, className)} ref={ref}>

        <div className={styles.SearchInput}>
            <button className={styles.SearchButton}><LocalImage src="/SearchIcon.svg" alt="Søk" width={20.563} height={20.317} /></button>
            <input type="text" onChange={changeEventHandler} onKeyDown={onKeyHandler} onFocus={() => locationState.dispatcher({ type: "toggleToolsDisplay", value: true })} /*onBlur={() => setIsOpen(false)}*/ className={styles.SearchField} placeholder="Søk etter sted eller fylke" value={inputValue} />
        </div>
        {locationState.isShowingLocationTools && <div className={styles.ExpandedContainer}>
            <button onClick={() => locationState.dispatcher({ type: "toggleToolsDisplay", value: false })} aria-label="Lukk menyen" className={styles.Close_Button}>
                <Close theme="dark" />
            </button>
            <div className={styles.ExpandedInnerContainer}>
                <LocationSearchResult searchString={debouncedInputValue} regionTheaters={data} dispatch={locationState.dispatcher} />
                <SelectedLocations dispatch={locationState.dispatcher} selectedLocations={locationState} />
                <RegionOverview dispatch={locationState.dispatcher} regionTheaters={data} />
            </div>
        </div>}
    </div>;
}
//#endregion
